import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { apiCallWithHost } from '../../utils/apiClient'

// Async thunk for fetching trainer data
export const fetchTrainerData = createAsyncThunk(
  'trainer/fetchTrainerData',
  async ({ token, externalId, profileId, forceRefresh = false }, { getState }) => {
    const state = getState()
    const trainerState = state.trainer || initialState
    const cachedTrainer = trainerState.dataByProfile[profileId]
    const lastFetched = trainerState.lastFetchedByProfile[profileId]

    // If we have cached data and it's less than 5 minutes old and not forcing refresh
    if (!forceRefresh && cachedTrainer && lastFetched) {
      const fiveMinutesAgo = Date.now() - 5 * 60 * 1000
      if (lastFetched > fiveMinutesAgo) {
        return { data: cachedTrainer, profileId }
      }
    }

    // Fetch fresh data from API
    const response = await apiCallWithHost(`/trainers/${externalId}`, {}, token)

    if (!response.ok) {
      throw new Error('Failed to fetch trainer data')
    }

    const data = await response.json()
    return { data, profileId }
  },
)

// Async thunk for updating trainer data

export const updateTrainerData = createAsyncThunk(
  'trainer/updateTrainerData',
  async ({ token, externalId, profileId, data }, { dispatch }) => {
    const response = await apiCallWithHost(
      `/trainers/${externalId}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
      token,
    )

    if (!response.ok) {
      throw new Error('Failed to update trainer data')
    }

    const updatedData = await response.json()
    return { data: updatedData, profileId }
  },
)

const initialState = {
  dataByProfile: {}, // Map of profileId to trainer data
  lastFetchedByProfile: {}, // Map of profileId to last fetch timestamp
  statusByProfile: {}, // Map of profileId to status
  errorByProfile: {}, // Map of profileId to error
}

const trainerSlice = createSlice({
  name: 'trainer',
  initialState,
  reducers: {
    clearTrainerData: (state, action) => {
      const profileId = action.payload
      if (profileId) {
        delete state.dataByProfile[profileId]
        delete state.lastFetchedByProfile[profileId]
        delete state.statusByProfile[profileId]
        delete state.errorByProfile[profileId]
      } else {
        // Clear all data if no profileId provided
        state.dataByProfile = {}
        state.lastFetchedByProfile = {}
        state.statusByProfile = {}
        state.errorByProfile = {}
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTrainerData.pending, (state, action) => {
        const profileId = action.meta.arg.profileId
        state.statusByProfile[profileId] = 'loading'
      })
      .addCase(fetchTrainerData.fulfilled, (state, action) => {
        const { data, profileId } = action.payload
        state.statusByProfile[profileId] = 'succeeded'
        state.dataByProfile[profileId] = data
        state.lastFetchedByProfile[profileId] = Date.now()
        state.errorByProfile[profileId] = null
      })
      .addCase(fetchTrainerData.rejected, (state, action) => {
        const profileId = action.meta.arg.profileId
        state.statusByProfile[profileId] = 'failed'
        state.errorByProfile[profileId] = action.error.message
      })
      .addCase(updateTrainerData.pending, (state, action) => {
        const profileId = action.meta.arg.profileId
        state.statusByProfile[profileId] = 'loading'
      })
      .addCase(updateTrainerData.fulfilled, (state, action) => {
        const { data, profileId } = action.payload
        state.statusByProfile[profileId] = 'succeeded'
        state.dataByProfile[profileId] = data
        state.lastFetchedByProfile[profileId] = Date.now()
        state.errorByProfile[profileId] = null
      })
      .addCase(updateTrainerData.rejected, (state, action) => {
        const profileId = action.meta.arg.profileId
        state.statusByProfile[profileId] = 'failed'
        state.errorByProfile[profileId] = action.error.message
      })
  },
})

export const { clearTrainerData } = trainerSlice.actions

// Selectors
export const getTrainerData = (state, profileId) => state.trainer?.dataByProfile[profileId] ?? null
export const getTrainerStatus = (state, profileId) =>
  state.trainer?.statusByProfile[profileId] ?? 'idle'
export const getTrainerError = (state, profileId) =>
  state.trainer?.errorByProfile[profileId] ?? null
export const getTrainerLastFetched = (state, profileId) =>
  state.trainer?.lastFetchedByProfile[profileId] ?? null

export default trainerSlice.reducer
